// Which keys each upsert changed, so selectors can re-test only those records. Maps are tracked by
// version number, so the log never keeps a map alive.

import type { Map as ImmutableMap } from 'immutable';

import { registerCacheReset } from 'app-components/utils/core/reselect/selectorCacheRegistry';

import { getDangerouslyUseFeatureFlags } from '../feature-flags/featureFlagsReducer';

type ResourceMap = ImmutableMap<string, unknown>;

/** The keys a change touched and whether it deleted any key, even one it then set again. */
export type ResourceChanges = { keys: ReadonlySet<string>; removed: boolean };

const mapVersions = new WeakMap<ResourceMap, number>();
const changeLog = new Map<number, { parent: number; keys: readonly string[] | null; removed: boolean }>();
// Versions keep growing across resets: live maps can outlast one (datasets on a dataset switch).
let lastVersion = 0;
registerCacheReset(() => changeLog.clear());
const MAX_LOGGED_CHANGES = 256;
const MAX_CHANGE_STEPS = 16;
/** Changes touching more keys are treated as bulk changes, which callers handle with a full scan. */
export const MAX_CHANGED_KEYS = 64;

/** A version number identifying this map, for getResourceChangesSince. */
export function getResourceVersion(resourcesById: ResourceMap): number {
  let version = mapVersions.get(resourcesById);
  if (version === undefined) {
    lastVersion += 1;
    version = lastVersion;
    mapVersions.set(resourcesById, version);
  }
  return version;
}

/** Logs that `next` is `previous` with only `keys` changed (null: a bulk change), and whether a key was deleted. */
export function logResourceChange(
  previous: ResourceMap,
  next: ResourceMap,
  keys: readonly string[] | null,
  removed: boolean,
) {
  if (previous === next) return;
  const parent = getResourceVersion(previous);
  lastVersion += 1;
  mapVersions.set(next, lastVersion);
  changeLog.set(lastVersion, { parent, keys: keys && keys.length > MAX_CHANGED_KEYS ? null : keys, removed });
  if (changeLog.size > MAX_LOGGED_CHANGES) {
    changeLog.delete(changeLog.keys().next().value!);
  }
}

/**
 * The keys whose values may differ between the map with `version` and `resourcesById`, or undefined
 * unless `resourcesById` came from that map through a few logged changes of a few keys each. Always
 * undefined while the LaunchDarkly kill switch is off, so every consumer recomputes from the full map.
 */
export function getResourceChangesSince(resourcesById: ResourceMap, version: number): ResourceChanges | undefined {
  // Selectors call this without the store, so it reads the loaded flags; flags that have not loaded
  // count as on. The switch changes how results are computed, never what they are.
  if (getDangerouslyUseFeatureFlags()?.get('transactionsIncrementalSelectorsRelease') === false) return undefined;
  const keys = new Set<string>();
  let removed = false;
  let current = mapVersions.get(resourcesById);
  for (let steps = 0; current !== version; steps += 1) {
    // Versions only grow along a chain, so an older or unknown map cannot lead back to `version`.
    if (current === undefined || current < version || steps === MAX_CHANGE_STEPS) return undefined;
    const entry = changeLog.get(current);
    if (!entry?.keys) return undefined;
    entry.keys.forEach((key) => keys.add(key));
    if (keys.size > MAX_CHANGED_KEYS) return undefined;
    removed = removed || entry.removed;
    current = entry.parent;
  }
  return { keys, removed };
}
